import React, { useState, useEffect, useCallback } from 'react';
import { useEntity } from '../contexts/EntityContext';
import { useLanguage } from '../contexts/LanguageContext';
import { API_URL } from '../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Trash2, Plus, AlertCircle, Check } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { label: 'Omzet diensten', code: '4000' },
  { label: 'Omzet SaaS', code: '4010' },
  { label: 'Omzet consultancy', code: '4020' },
  { label: 'Overige omzet', code: '4030' },
  { label: 'Marketing', code: '4400' },
  { label: 'Hosting & infra', code: '4100' },
  { label: 'Uitbesteed werk', code: '4200' },
  { label: 'Salarissen', code: '7000' },
  { label: 'Freelancers', code: '7100' },
  { label: 'Huisvesting', code: '7500' },
  { label: 'Kantoor', code: '7600' },
  { label: 'Software', code: '7700' },
  { label: 'Overig', code: '7800' },
  { label: 'Advieskosten', code: '7900' },
  { label: 'Bankkosten', code: '8200' },
];

const ACCOUNT_MAP = {
  '0010': 'Gestort kapitaal', '0020': 'Agioreserve', '0030': 'Reserves', '0040': 'Onverdeeld resultaat',
  '1000': 'Bank', '1100': 'Spaarrekening', '1300': 'Debiteuren', '1400': 'Crediteuren',
  '1600': 'BTW te vorderen', '1700': 'BTW te betalen',
  '4000': 'Omzet diensten', '4010': 'Omzet SaaS', '4020': 'Omzet consultancy', '4030': 'Overige omzet',
  '4100': 'Hosting & infra', '4200': 'Uitbesteed werk', '4300': 'Overige directe kosten',
  '4400': 'Advertentiekosten',
  '7000': 'Salarissen en lonen', '7010': 'Sociale lasten', '7020': 'Pensioenlasten',
  '7100': 'Freelancers', '7500': 'Huisvestingskosten',
  '7600': 'Kantoorkosten', '7700': 'Software / ICT',
  '7800': 'Algemene kosten', '7900': 'Advieskosten',
  '8000': 'Afschrijvingen materieel', '8010': 'Afschrijvingen immaterieel',
  '8100': 'Rentelasten', '8110': 'Rentebaten', '8200': 'Bankkosten', '8300': 'Valutaresultaten',
};

export function Bookkeeping() {
  const { selectedEntity } = useEntity();
  const { language } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bookingType, setBookingType] = useState('expense'); // expense, revenue, custom
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    category: '7700',
    amount: '',
    vatRate: '21',
    includesVat: true,
  });

  const fetchEntries = useCallback(async () => {
    if (!selectedEntity) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/journal-entries?entity_id=${selectedEntity.entity_id}&limit=100`, {
        credentials: 'include'
      });
      if (res.ok) setEntries(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [selectedEntity]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const resetForm = () => {
    setForm({
      date: new Date().toISOString().split('T')[0],
      description: '',
      category: bookingType === 'revenue' ? '4000' : '7700',
      amount: '',
      vatRate: '21',
      includesVat: true,
    });
  };

  const handleQuickBook = async (e) => {
    e.preventDefault();
    const totalAmount = parseFloat(form.amount);
    if (!totalAmount || totalAmount <= 0) {
      toast.error('Voer een geldig bedrag in');
      return;
    }

    const vatRate = parseFloat(form.vatRate) / 100;
    let netAmount, vatAmount;
    
    if (form.includesVat && vatRate > 0) {
      netAmount = Math.round((totalAmount / (1 + vatRate)) * 100) / 100;
      vatAmount = Math.round((totalAmount - netAmount) * 100) / 100;
    } else if (vatRate > 0) {
      netAmount = totalAmount;
      vatAmount = Math.round(totalAmount * vatRate * 100) / 100;
    } else {
      netAmount = totalAmount;
      vatAmount = 0;
    }

    const totalIncVat = netAmount + vatAmount;
    let lines = [];

    if (bookingType === 'expense') {
      // Expense: debit expense + debit BTW te vorderen, credit Bank
      lines.push({ account_code: form.category, debit: netAmount, credit: 0 });
      if (vatAmount > 0) {
        lines.push({ account_code: '1600', debit: vatAmount, credit: 0 });
      }
      lines.push({ account_code: '1000', debit: 0, credit: totalIncVat });
    } else if (bookingType === 'revenue') {
      // Revenue: debit Bank, credit Revenue + credit BTW te betalen
      lines.push({ account_code: '1000', debit: totalIncVat, credit: 0 });
      lines.push({ account_code: form.category, debit: 0, credit: netAmount });
      if (vatAmount > 0) {
        lines.push({ account_code: '1700', debit: 0, credit: vatAmount });
      }
    }

    try {
      const res = await fetch(`${API_URL}/api/journal-entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          entity_id: selectedEntity.entity_id,
          date: new Date(form.date).toISOString(),
          description: form.description,
          lines,
          source: 'manual',
          currency: selectedEntity.country === 'NL' ? 'EUR' : 'USD'
        })
      });
      if (res.ok) {
        toast.success('Boeking opgeslagen');
        setDialogOpen(false);
        resetForm();
        fetchEntries();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fout bij opslaan');
      }
    } catch (err) {
      toast.error('Netwerkfout');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Weet je zeker dat je deze boeking wilt verwijderen?')) return;
    try {
      const res = await fetch(`${API_URL}/api/journal-entries/${entryId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('Boeking verwijderd');
        fetchEntries();
      }
    } catch (err) {
      toast.error('Fout bij verwijderen');
    }
  };

  if (!selectedEntity) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">{language === 'nl' ? 'Selecteer een entiteit' : 'Select an entity'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bookkeeping-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {language === 'nl' ? 'Boekhouding' : 'Bookkeeping'}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">{selectedEntity.name}</p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="new-expense-btn"
            variant="outline"
            onClick={() => { setBookingType('expense'); resetForm(); setForm(f => ({ ...f, category: '7700' })); setDialogOpen(true); }}
          >
            <Plus className="w-4 h-4 mr-1" /> Kosten
          </Button>
          <Button
            data-testid="new-revenue-btn"
            className="btn-brand"
            onClick={() => { setBookingType('revenue'); resetForm(); setForm(f => ({ ...f, category: '4000' })); setDialogOpen(true); }}
          >
            <Plus className="w-4 h-4 mr-1" /> Omzet
          </Button>
        </div>
      </div>

      {/* Journal entries list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {language === 'nl' ? 'Journaalposten' : 'Journal Entries'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground text-center py-8">Laden...</p>
          ) : entries.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">
                {language === 'nl' ? 'Nog geen boekingen. Maak je eerste boeking aan.' : 'No entries yet. Create your first entry.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {entries.map(entry => (
                <div key={entry.entry_id} className="border rounded-lg p-4 hover:bg-muted/30 transition-colors" data-testid={`entry-${entry.entry_id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(entry.date).toLocaleDateString('nl-NL')}
                      </span>
                      {entry.reference && (
                        <span className="text-xs text-muted-foreground ml-2">ref: {entry.reference}</span>
                      )}
                      <h3 className="font-medium">{entry.description}</h3>
                      <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground mt-1">
                        {entry.source}
                      </span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.entry_id)}>
                      <Trash2 className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </div>
                  {/* Debit/Credit lines */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-muted-foreground border-b">
                          <th className="text-left py-1 pr-4">{language === 'nl' ? 'Rekening' : 'Account'}</th>
                          <th className="text-right py-1 px-4 w-24">Debet</th>
                          <th className="text-right py-1 pl-4 w-24">Credit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(entry.lines || []).map((line, i) => (
                          <tr key={i} className="border-b border-dashed last:border-0">
                            <td className="py-1 pr-4">
                              <span className="text-muted-foreground mr-1">{line.account_code}</span>
                              {line.account_name || ACCOUNT_MAP[line.account_code] || ''}
                            </td>
                            <td className="text-right py-1 px-4 tabular-nums">
                              {line.debit > 0 ? `${entry.currency === 'USD' ? '$' : '\u20AC'}${line.debit.toFixed(2)}` : ''}
                            </td>
                            <td className="text-right py-1 pl-4 tabular-nums">
                              {line.credit > 0 ? `${entry.currency === 'USD' ? '$' : '\u20AC'}${line.credit.toFixed(2)}` : ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick booking dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {bookingType === 'expense' ? (language === 'nl' ? 'Kosten boeken' : 'Book Expense') : (language === 'nl' ? 'Omzet boeken' : 'Book Revenue')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleQuickBook} className="space-y-4">
            <div>
              <Label>Datum</Label>
              <Input
                data-testid="booking-date"
                type="date"
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Omschrijving</Label>
              <Input
                data-testid="booking-description"
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder={bookingType === 'expense' ? 'Bijv. Google Ads februari' : 'Bijv. Factuur klant X'}
                required
              />
            </div>

            {bookingType === 'expense' && (
              <div>
                <Label>Categorie</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="booking-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.filter(c => !c.code.startsWith('40') || c.code === '4100' || c.code === '4200' || c.code === '4400').map(c => (
                      <SelectItem key={c.code} value={c.code}>{c.code} - {c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {bookingType === 'revenue' && (
              <div>
                <Label>Omzetcategorie</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="booking-revenue-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.filter(c => c.code === '4000' || c.code === '4010' || c.code === '4020' || c.code === '4030').map(c => (
                      <SelectItem key={c.code} value={c.code}>{c.code} - {c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Bedrag ({selectedEntity.country === 'NL' ? '\u20AC' : '$'})</Label>
                <Input
                  data-testid="booking-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.amount}
                  onChange={e => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              <div>
                <Label>BTW %</Label>
                <Select value={form.vatRate} onValueChange={v => setForm({ ...form, vatRate: v })}>
                  <SelectTrigger data-testid="booking-vat">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="21">21%</SelectItem>
                    <SelectItem value="9">9%</SelectItem>
                    <SelectItem value="0">0% (vrijgesteld)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.vatRate !== '0' && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="includesVat"
                  checked={form.includesVat}
                  onChange={e => setForm({ ...form, includesVat: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="includesVat" className="text-sm text-muted-foreground">
                  Bedrag is inclusief BTW
                </label>
              </div>
            )}

            {/* Preview */}
            {form.amount && parseFloat(form.amount) > 0 && (
              <PreviewLines
                bookingType={bookingType}
                amount={parseFloat(form.amount)}
                vatRate={parseFloat(form.vatRate) / 100}
                includesVat={form.includesVat}
                category={form.category}
                currency={selectedEntity.country === 'NL' ? 'EUR' : 'USD'}
              />
            )}

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
                Annuleren
              </Button>
              <Button type="submit" className="flex-1 btn-brand" data-testid="save-booking-btn">
                <Check className="w-4 h-4 mr-1" /> Boeken
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PreviewLines({ bookingType, amount, vatRate, includesVat, category, currency }) {
  let netAmount, vatAmount, totalIncVat;

  if (includesVat && vatRate > 0) {
    netAmount = Math.round((amount / (1 + vatRate)) * 100) / 100;
    vatAmount = Math.round((amount - netAmount) * 100) / 100;
  } else if (vatRate > 0) {
    netAmount = amount;
    vatAmount = Math.round(amount * vatRate * 100) / 100;
  } else {
    netAmount = amount;
    vatAmount = 0;
  }
  totalIncVat = netAmount + vatAmount;
  const sym = currency === 'USD' ? '$' : '\u20AC';

  const lines = bookingType === 'expense' ? [
    { account: `${category} ${ACCOUNT_MAP[category] || ''}`, debit: netAmount, credit: 0 },
    ...(vatAmount > 0 ? [{ account: '1600 BTW te vorderen', debit: vatAmount, credit: 0 }] : []),
    { account: '1000 Bank', debit: 0, credit: totalIncVat },
  ] : [
    { account: '1000 Bank', debit: totalIncVat, credit: 0 },
    { account: `${category} ${ACCOUNT_MAP[category] || 'Omzet'}`, debit: 0, credit: netAmount },
    ...(vatAmount > 0 ? [{ account: '1700 BTW te betalen', debit: 0, credit: vatAmount }] : []),
  ];

  return (
    <div className="bg-muted/50 rounded-lg p-3">
      <p className="text-xs font-semibold text-muted-foreground mb-2">Boeking preview</p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-muted-foreground">
            <th className="text-left">Rekening</th>
            <th className="text-right w-20">Debet</th>
            <th className="text-right w-20">Credit</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              <td className="py-0.5">{l.account}</td>
              <td className="text-right tabular-nums">{l.debit > 0 ? `${sym}${l.debit.toFixed(2)}` : ''}</td>
              <td className="text-right tabular-nums">{l.credit > 0 ? `${sym}${l.credit.toFixed(2)}` : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
